/**
 * Intellispot Entity Extractor - LLM-Based Semantic Extraction
 *
 * Uses Claude AI to extract and categorize entities from business documents
 * based on the Market-Business-Solution ontology.
 *
 * The system maps data into three hierarchical layers:
 * - MARKET (Green): The "Who" and "Why" - Customers, Pains, Jobs, Competitors
 * - BUSINESS (Yellow): The "What" and "How" - Value Props, Channels, Revenue
 * - SOLUTION (Blue): The "Viability" - Products, Features, Requirements
 */

import type { OntologyClass, OntologyDomain } from '@/types/ontology'
import { MARKET_CLASSES, BUSINESS_CLASSES, SOLUTION_CLASSES } from '@/lib/ontology/schema'
import { callClaude, parseJsonResponse, type ClaudeConfig } from '@/lib/ai/claude'

// ============================================================================
// LLM OUTPUT LOGGING
// ============================================================================

const LOG_STORAGE_KEY = 'pdfProcessor_llm_logs'
const MAX_LOGS = 50 // Keep last 50 extractions

interface LLMLogEntry {
  timestamp: string
  model: string
  documentLength: number
  responseLength: number
  entityCount: number
  systemPrompt: string
  userPrompt: string
  rawResponse: string
  parsedResult: LLMExtractionResult | null
  error?: string
}

function saveLLMLog(entry: LLMLogEntry): void {
  try {
    const existing = localStorage.getItem(LOG_STORAGE_KEY)
    const logs: LLMLogEntry[] = existing ? JSON.parse(existing) : []

    // Add new entry at the beginning
    logs.unshift(entry)

    // Keep only last MAX_LOGS entries
    if (logs.length > MAX_LOGS) {
      logs.length = MAX_LOGS
    }

    localStorage.setItem(LOG_STORAGE_KEY, JSON.stringify(logs))
    console.log(`[PdfProcessor] LLM output logged (${logs.length} total logs)`)
  } catch (err) {
    console.warn('[PdfProcessor] Failed to save LLM log:', err)
  }
}

/**
 * Get all stored LLM logs
 */
export function getLLMLogs(): LLMLogEntry[] {
  try {
    const existing = localStorage.getItem(LOG_STORAGE_KEY)
    return existing ? JSON.parse(existing) : []
  } catch {
    return []
  }
}

/**
 * Export logs as downloadable JSON file
 */
export function exportLLMLogs(): void {
  const logs = getLLMLogs()
  const blob = new Blob([JSON.stringify(logs, null, 2)], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `pdfprocessor-llm-logs-${new Date().toISOString().slice(0, 10)}.json`
  a.click()
  URL.revokeObjectURL(url)
}

/**
 * Clear all stored logs
 */
export function clearLLMLogs(): void {
  localStorage.removeItem(LOG_STORAGE_KEY)
  console.log('[PdfProcessor] LLM logs cleared')
}

// ============================================================================
// TYPES
// ============================================================================

export interface ExtractedEntity {
  id: string
  text: string
  suggestedClass: OntologyClass
  confidence: 'high' | 'medium' | 'low'
  context: string
  domain: OntologyDomain
}

export interface ExtractionResult {
  entities: ExtractedEntity[]
  unmatchedSentences: string[]
}

export interface LLMExtractionResult {
  // Market Layer (Green) - The "Who" and "Why"
  Industry?: string[]
  Competitor?: string[]
  Trend?: string[]
  MarketSignal?: string[]
  CustomerSegment?: string[]
  Persona?: string[]
  CustomerPain?: string[]
  CustomerGain?: string[]
  CustomerJob?: string[]

  // Business Layer (Yellow) - The "What" and "How"
  ValueProposition?: string[]
  Channel?: string[]
  RevenueStream?: string[]
  KeyResource?: string[]
  KeyActivity?: string[]
  KeyPartnership?: string[]
  CostStructure?: string[]
  Strategy?: string[]
  StrategicGoal?: string[]
  Strength?: string[]
  Weakness?: string[]
  Opportunity?: string[]
  Threat?: string[]

  // Solution Layer (Blue) - The "Viability"
  Product?: string[]
  Theme?: string[]
  Epic?: string[]
  Feature?: string[]
  UserStory?: string[]
  Requirement?: string[]
  Constraint?: string[]
}

// ============================================================================
// ONTOLOGY SCHEMA FOR LLM
// ============================================================================

const ONTOLOGY_SCHEMA = `
## Layer 1: Market Space (Green) - The "Who" and "Why"
Focus: External environment, customers, and market dynamics

- **Industry**: The broader sector or vertical (e.g., "FinTech", "Healthcare SaaS", "Enterprise HR")
- **Competitor**: Direct market rivals or alternative solutions (e.g., "Salesforce", "manual Excel processes")
- **Trend**: Market movements and shifts (e.g., "AI adoption", "remote work", "sustainability")
- **MarketSignal**: Early indicators of change (e.g., "increased patent filings in AI", "VC investments in climate tech")
- **CustomerSegment**: Target audience demographics (e.g., "SMEs in financial services", "Enterprise HR managers")
- **Persona**: The economic decision-maker (Buyer) or daily operator (User) (e.g., "CFO", "IT Manager", "End User")
- **CustomerPain**: Core friction, obstacle, or inefficiency (e.g., "manual data entry", "compliance risk", "slow reporting")
- **CustomerGain**: Desired outcomes and benefits (e.g., "save time", "reduce errors", "better insights")
- **CustomerJob**: The functional or emotional goal (JTBD) (e.g., "prepare monthly reports", "ensure compliance")

## Layer 2: Business Space (Yellow) - The "What" and "How"
Focus: Business model, strategy, and organizational configuration

- **ValueProposition**: The core promise of value, USP, differentiator (e.g., "automated compliance in one click")
- **Channel**: Distribution paths and GTM (e.g., "Direct Sales", "Partner Network", "App Store", "Self-service")
- **RevenueStream**: Pricing and monetization model (e.g., "Monthly SaaS subscription", "Usage-based pricing", "Freemium")
- **KeyResource**: Critical assets (e.g., "proprietary AI model", "customer data", "engineering team")
- **KeyActivity**: Essential operations (e.g., "platform development", "customer onboarding", "data processing")
- **KeyPartnership**: Strategic allies and integrations (e.g., "AWS", "Salesforce integration", "consulting partners")
- **CostStructure**: Major cost drivers (e.g., "cloud hosting", "sales team", "R&D", "customer support")
- **Strategy**: Strategic direction and vision (e.g., "become market leader in SMB automation")
- **StrategicGoal**: Measurable objectives (e.g., "reach $10M ARR by Q4", "expand to EU market")
- **Strength**: Internal advantages (SWOT) (e.g., "strong brand", "patented technology")
- **Weakness**: Internal limitations (SWOT) (e.g., "limited sales team", "technical debt")
- **Opportunity**: External growth potential (SWOT) (e.g., "new market segment", "regulatory tailwind")
- **Threat**: External risks (SWOT) (e.g., "new competitors", "economic downturn")

## Layer 3: Solution Space (Blue) - The "Viability"
Focus: Product and technical implementation

- **Product**: The solution, platform, or application name (e.g., "Intellispot Platform", "Analytics Dashboard")
- **Theme**: High-level strategic product initiative (e.g., "AI-powered automation", "Mobile-first experience")
- **Epic**: Large feature set or capability (e.g., "Automated reporting module", "User management system")
- **Feature**: Specific functionality (e.g., "one-click export to PDF", "real-time notifications")
- **UserStory**: Use case from user perspective (e.g., "As a manager, I want to see weekly summaries")
- **Requirement**: Functional or non-functional requirements (e.g., "must support 10,000 concurrent users")
- **Constraint**: Technical, regulatory, or business constraints (e.g., "GDPR compliance", "legacy system integration")
`

// ============================================================================
// SYSTEM PROMPT - The "Brain" of the Extraction
// ============================================================================

const SYSTEM_PROMPT = `You are a Senior Strategic Analyst (McKinsey/BCG level) specializing in business strategy, market analysis, and product management.

Your task is to analyze business documents and extract structured data points that map to a specific ontology schema. You must:

1. **EXTRACT**: Identify explicit mentions of concepts in the text
2. **INFER**: Use strategic reasoning to infer implicit information:
   - If "SaaS" or "subscription" is mentioned, infer RevenueStream = ["Subscription-based pricing"]
   - If "AWS" or "cloud" is mentioned, infer CostStructure = ["Cloud infrastructure"]
   - If no competitors named but "versus" or "unlike" appears, infer the comparison target
   - If "manual processes" or "spreadsheets" mentioned as current state, add to Competitor as "Manual Processes"
   - If a pricing model is mentioned, infer related RevenueStream
   - If technology stack mentioned, infer KeyResource

3. **CATEGORIZE**: Map each extracted item to the correct ontology class

${ONTOLOGY_SCHEMA}

## Output Format
Return a JSON object where:
- Keys are the exact entity type names from the schema above (e.g., "CustomerPain", "ValueProposition")
- Values are arrays of strings, each string being a specific instance found or inferred
- Only include keys that have data (non-empty arrays)
- Keep each item concise but descriptive (5-50 words)
- Do not duplicate items across categories

## Example Output
{
  "Industry": ["B2B SaaS for Financial Services"],
  "CustomerSegment": ["Mid-market CFOs", "Enterprise Finance Teams"],
  "CustomerPain": ["Manual data reconciliation takes 40+ hours monthly", "High error rate in financial reporting"],
  "CustomerGain": ["Reduce reporting time by 80%", "Real-time financial visibility"],
  "ValueProposition": ["Automated financial reconciliation with AI-powered matching"],
  "RevenueStream": ["Monthly SaaS subscription", "Enterprise licensing"],
  "Competitor": ["Legacy ERP systems", "Manual Excel processes"],
  "Feature": ["One-click bank reconciliation", "Automated variance analysis"]
}

Remember: Think strategically. Infer what's implied, not just what's explicitly stated.`

// ============================================================================
// EXTRACTION FUNCTIONS
// ============================================================================

/**
 * Prepare document text for LLM processing
 * - Limit to first 50 pages worth of content
 * - Truncate to fit context window (80,000 chars)
 */
function prepareDocumentText(text: string, maxChars: number = 80000): string {
  // Normalize whitespace
  let cleaned = text
    .replace(/\r\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .replace(/[ \t]+/g, ' ')
    .trim()

  // Truncate if too long
  if (cleaned.length > maxChars) {
    cleaned = cleaned.slice(0, maxChars)
    // Try to end at a sentence boundary
    const lastPeriod = cleaned.lastIndexOf('.')
    if (lastPeriod > maxChars - 500) {
      cleaned = cleaned.slice(0, lastPeriod + 1)
    }
  }

  return cleaned
}

/**
 * Get ontology class by local name
 */
function getClassByLocalName(localName: string): OntologyClass | undefined {
  const allClasses = [...MARKET_CLASSES, ...BUSINESS_CLASSES, ...SOLUTION_CLASSES]
  return allClasses.find((c) => c.localName === localName)
}

/**
 * Convert LLM extraction result to ExtractedEntity array
 */
function convertToEntities(llmResult: LLMExtractionResult): ExtractedEntity[] {
  const entities: ExtractedEntity[] = []
  let entityId = 0

  for (const [className, items] of Object.entries(llmResult)) {
    if (!Array.isArray(items) || items.length === 0) continue

    const ontologyClass = getClassByLocalName(className)
    if (!ontologyClass) {
      console.warn(`Unknown ontology class: ${className}`)
      continue
    }

    for (const item of items) {
      if (typeof item !== 'string' || item.trim().length === 0) continue

      entities.push({
        id: `entity-${entityId++}`,
        text: item.trim(),
        suggestedClass: ontologyClass,
        confidence: 'high', // LLM extractions are considered high confidence
        context: `Extracted by AI as ${ontologyClass.label}`,
        domain: ontologyClass.domain,
      })
    }
  }

  return entities
}

/**
 * Extract entities using Claude LLM
 */
export async function extractEntitiesWithLLM(
  text: string,
  apiKey: string,
  model?: string
): Promise<ExtractionResult> {
  const config: ClaudeConfig = {
    apiKey,
    model: model || 'claude-sonnet-4-20250514',
    maxTokens: 4096,
  }

  const documentText = prepareDocumentText(text)

  const userMessage = `Analyze the following business document and extract all relevant entities according to the ontology schema. Remember to both extract explicit mentions AND infer implicit information using strategic reasoning.

---
DOCUMENT:
${documentText}
---

Extract all entities and return as JSON.`

  try {
    const response = await callClaude(config, SYSTEM_PROMPT, userMessage)
    const llmResult = parseJsonResponse<LLMExtractionResult>(response)

    // Log the LLM output
    const entities = llmResult ? convertToEntities(llmResult) : []
    saveLLMLog({
      timestamp: new Date().toISOString(),
      model: config.model || 'claude-sonnet-4-20250514',
      documentLength: documentText.length,
      responseLength: response.length,
      entityCount: entities.length,
      systemPrompt: SYSTEM_PROMPT,
      userPrompt: userMessage,
      rawResponse: response,
      parsedResult: llmResult,
    })

    if (!llmResult) {
      console.error('Failed to parse LLM response')
      return { entities: [], unmatchedSentences: [] }
    }

    return {
      entities,
      unmatchedSentences: [], // LLM processes entire document
    }
  } catch (error) {
    // Log error case
    saveLLMLog({
      timestamp: new Date().toISOString(),
      model: config.model || 'claude-sonnet-4-20250514',
      documentLength: documentText.length,
      responseLength: 0,
      entityCount: 0,
      systemPrompt: SYSTEM_PROMPT,
      userPrompt: userMessage,
      rawResponse: '',
      parsedResult: null,
      error: error instanceof Error ? error.message : 'Unknown error',
    })

    console.error('LLM extraction failed:', error)
    throw error
  }
}

// ============================================================================
// FALLBACK: Rule-based extraction (when no API key)
// ============================================================================

/**
 * Simple rule-based extraction as fallback
 */
export function extractEntitiesFromText(text: string): ExtractionResult {
  const entities: ExtractedEntity[] = []
  const unmatchedSentences: string[] = []

  // Simple keyword-based extraction as fallback
  const patterns: Array<{ pattern: RegExp; className: string }> = [
    { pattern: /pain\s*points?[:=]?\s*([^.]+)/gi, className: 'CustomerPain' },
    { pattern: /challenge[:=]?\s*([^.]+)/gi, className: 'CustomerPain' },
    { pattern: /value\s*proposition[:=]?\s*([^.]+)/gi, className: 'ValueProposition' },
    { pattern: /competitor[:=]?\s*([^.]+)/gi, className: 'Competitor' },
    { pattern: /target\s*(?:market|segment|customer)[:=]?\s*([^.]+)/gi, className: 'CustomerSegment' },
    { pattern: /feature[:=]?\s*([^.]+)/gi, className: 'Feature' },
    { pattern: /revenue\s*(?:stream|model)[:=]?\s*([^.]+)/gi, className: 'RevenueStream' },
  ]

  let entityId = 0

  for (const { pattern, className } of patterns) {
    let match
    while ((match = pattern.exec(text)) !== null) {
      const ontologyClass = getClassByLocalName(className)
      if (ontologyClass && match[1]) {
        entities.push({
          id: `entity-${entityId++}`,
          text: match[1].trim().slice(0, 100),
          suggestedClass: ontologyClass,
          confidence: 'low',
          context: match[0].slice(0, 200),
          domain: ontologyClass.domain,
        })
      }
    }
  }

  return { entities, unmatchedSentences }
}

// ============================================================================
// URI GENERATION
// ============================================================================

export function generateInstanceUri(_className: string, instanceName: string): string {
  const prefix = 'http://intellispot.ai/data/'
  const cleanName = instanceName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 50)

  return `${prefix}${cleanName}-${Date.now()}`
}
